/**
 * /for-investors landing page.
 *
 * Layout (revised): the THREE persona cards are the hero — they're the
 * highly-visual hook, so they sit at the very top, right under the headline.
 * Picking a person (Marcus · Sloan[center] · Mateo) opens a callback form
 * (name + phone + two-checkbox TCPA consent) and that AI persona CALLS YOU
 * BACK from (414) 310-0078 via POST /api/v1/voice/request-callback.
 *
 * There is exactly ONE chatbox — a single full-width AudienceIntakeChat below
 * the proof row ("Prefer to type?"), the same intake/attribution flow the
 * family-office / fund pages use. (We deliberately do NOT use the shared
 * <Hero>, which embeds its own chat — that would be a second chatbox.)
 *
 * Page flow: hero (headline + persona cards) → proof → full-width chatbox →
 * book card → features → closing CTA.
 *
 * Persona tiles are image-forward, modeled on the mens-health PersonaCard:
 * a gradient + icon fallback renders until /personas/{id}.png exists, then
 * the tile self-upgrades. /family-offices and /for-funds are unaffected.
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarCheck, ArrowRight } from 'lucide-react';
import {
  TopBar,
  ProofRow,
  FeatureBand,
  ClosingCTA,
  Footer,
  fadeUp,
  fadeIn,
  resolveUtm,
  type UtmParams,
} from './AudienceLandingPage';
import AudienceIntakeChat from '../components/AudienceIntakeChat';
import { INVESTORS_CONFIG } from '../data/audienceLandingConfig';
import { INVESTOR_PERSONAS, type InvestorPersona } from '../data/investorPersonas';
import PersonaCard from '../components/PersonaCard';
import PersonaCallbackForm from '../components/PersonaCallbackForm';

/**
 * Hero — headline + the three persona cards (the visual centerpiece, up top).
 * Picking a persona swaps the grid for the callback form in place.
 */
const PersonaHero: React.FC<{
  selected: InvestorPersona | null;
  onSelect: (p: InvestorPersona) => void;
  onBack: () => void;
}> = ({ selected, onSelect, onBack }) => {
  const config = INVESTORS_CONFIG;
  return (
    <section className="relative bg-apple-bg px-6 pt-24 pb-12">
      <motion.div initial="hidden" animate="show" variants={fadeIn} className="mx-auto max-w-5xl text-center">
        <motion.p
          variants={fadeUp}
          className="text-sm font-medium uppercase tracking-widest text-apple-subtext"
        >
          {config.heroEyebrow}
        </motion.p>
        <motion.h1
          variants={fadeUp}
          transition={{ delay: 0.1 }}
          className="mt-6 text-5xl font-semibold tracking-tight text-apple-ink sm:text-display-2 md:text-display-1"
        >
          {config.heroHeadline}
        </motion.h1>
        <motion.p
          variants={fadeUp}
          transition={{ delay: 0.2 }}
          className="mx-auto mt-6 max-w-2xl text-lg text-apple-subtext sm:text-subhead"
        >
          {config.heroSub}
        </motion.p>

        {selected ? (
          <div className="mx-auto mt-10 max-w-xl">
            <PersonaCallbackForm persona={selected} onBack={onBack} />
          </div>
        ) : (
          <>
            {config.engage?.voiceTitle && (
              <motion.p
                variants={fadeUp}
                transition={{ delay: 0.3 }}
                className="mx-auto mt-10 max-w-xl text-base font-medium text-apple-ink"
              >
                {config.engage.voiceTitle}
              </motion.p>
            )}
            {config.engage?.voiceBody && (
              <motion.p
                variants={fadeUp}
                transition={{ delay: 0.35 }}
                className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-apple-subtext"
              >
                {config.engage.voiceBody}
              </motion.p>
            )}
            <motion.div
              variants={fadeUp}
              transition={{ delay: 0.4 }}
              className="mx-auto mt-8 grid max-w-3xl grid-cols-1 items-center gap-5 sm:grid-cols-3"
            >
              {INVESTOR_PERSONAS.map((p) => (
                <div
                  key={p.id}
                  // Flagship (Sloan) leads on mobile; on desktop the natural
                  // source order (Marcus · Sloan · Mateo) puts it in the center.
                  className={p.flagship ? 'order-first sm:order-none' : ''}
                >
                  <PersonaCard persona={p} selected={false} onSelect={onSelect} />
                </div>
              ))}
            </motion.div>
          </>
        )}
      </motion.div>
    </section>
  );
};

/**
 * The single chatbox ("Prefer to type?") + the book-a-walkthrough card.
 * No persona cards here — they live in the hero now.
 */
const ChatAndBookSection: React.FC<{ utm: UtmParams }> = ({ utm }) => {
  const config = INVESTORS_CONFIG;
  return (
    <section className="bg-apple-bg px-6 py-section">
      {/* The one and only chatbox. */}
      <motion.div
        id="page-chat"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        className="mx-auto max-w-5xl"
      >
        <div className="text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-apple-ink sm:text-headline">
            Prefer to type? Chat with us now.
          </h2>
          {config.engage?.chatBody && (
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-apple-subtext">
              {config.engage.chatBody}
            </p>
          )}
        </div>
        <div className="mt-8">
          <AudienceIntakeChat
            open
            mode="inline"
            audience={config.slug}
            audienceLabel={config.audienceLabel}
            utm={utm}
          />
        </div>
      </motion.div>

      {/* Book a walkthrough. */}
      {config.engage && (
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="mx-auto mt-12 max-w-3xl"
        >
          <div className="flex flex-col items-start gap-3 rounded-3xl border border-apple-divider bg-white p-7 shadow-card sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <CalendarCheck className="mt-0.5 h-7 w-7 shrink-0 text-apple-blue" aria-hidden="true" />
              <div>
                <h3 className="text-xl font-semibold tracking-tight text-apple-ink">
                  {config.engage.bookTitle}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-apple-subtext">
                  {config.engage.bookBody}
                </p>
              </div>
            </div>
            <a
              href={config.engage.bookHref}
              className="group inline-flex shrink-0 items-center gap-2 rounded-md text-sm font-medium text-apple-blue hover:text-apple-blueHover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-apple-blue focus-visible:ring-offset-2 focus-visible:ring-offset-apple-bg"
            >
              Grab a time
              <ArrowRight
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </a>
          </div>
        </motion.div>
      )}
    </section>
  );
};

const InvestorLandingPage: React.FC = () => {
  // Lazy-once UTM resolution, same as the shared page, so attribution flows
  // into the intake chat + callback identically.
  const [utm] = useState<UtmParams>(resolveUtm);
  const [selected, setSelected] = useState<InvestorPersona | null>(null);
  const config = INVESTORS_CONFIG;

  return (
    <main className="bg-apple-bg">
      <TopBar />
      <PersonaHero
        selected={selected}
        onSelect={setSelected}
        onBack={() => setSelected(null)}
      />
      <ProofRow config={config} />
      <ChatAndBookSection utm={utm} />
      {config.features.map((f, i) => (
        <FeatureBand key={i} feature={f} dark={i % 2 === 0} />
      ))}
      <ClosingCTA />
      <Footer />
    </main>
  );
};

export default InvestorLandingPage;
